import {useMemo} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {queryOptions, useQueries, type Query, type QueryClient} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import type {Persister} from '@github-ui/react-query-persister/types'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'

import {copilotChatRepositoryPath} from '../utils/api-url-helpers'

export interface RepoInfo {
  id: number
  name: string
  ownerLogin: string
  description?: string | null
  visibility?: string
  primaryLanguage?: string
  /** Set only for incomplete repository data seeded from the SSR payload. */
  partial?: boolean
}

type SSRRepoInfo = Pick<RepoInfo, 'id' | 'name' | 'ownerLogin'>

interface UseRepoInfoOptions {
  requireFullDetails?: boolean
  persister?: Persister
}

// Only complete repository records are restored. A failed lookup is cached as `null`, which the offline cache treats
// as a miss and deletes, so it is retried on the next page load instead of being restored.
const RepoInfoValidator = Compile(
  Type.Object({id: Type.Number(), name: Type.String(), ownerLogin: Type.String()}, {additionalProperties: true}),
)

/** IndexedDB persister for repository info, revalidated in the background once restored data is stale. */
export function createRepoInfoPersister({
  sendAnalyticsEvent,
  enabled = true,
  partition,
}: {
  sendAnalyticsEvent: SendAnalyticsEventFunction
  enabled?: boolean
  /** Keeps entries saved in different contexts, such as under different SSO access, apart. */
  partition?: string
}): Persister {
  return createPersister({
    validator: RepoInfoValidator,
    sendAnalyticsEvent,
    prefix: partition === undefined ? 'agent-sessions-repo-info' : `agent-sessions-repo-info-${partition}`,
    maxAgeMs: 1000 * 60 * 60 * 24,
    alwaysRefetch: false,
    enabled,
  })
}

export function seedRepoInfoCache(queryClient: QueryClient, repositories: readonly SSRRepoInfo[]): void {
  for (const repo of repositories) {
    queryClient.setQueryData<RepoInfo | null>(['repo-info', repo.id], cachedRepo =>
      cachedRepo && cachedRepo.partial !== true ? {...cachedRepo, ...repo} : {...repo, partial: true},
    )
  }
}

async function fetchRepoInfo(repoID: number): Promise<RepoInfo | null> {
  try {
    const res = await verifiedFetchJSON(copilotChatRepositoryPath(repoID), {method: 'GET'})
    if (!res.ok) return null
    const payload = await res.json()
    const primaryLanguage = Array.isArray(payload.languages) ? payload.languages[0]?.name : undefined
    return {
      id: payload.id,
      name: payload.name,
      ownerLogin: payload.owner?.login ?? payload.ownerLogin,
      ...(payload.description === null || typeof payload.description === 'string'
        ? {description: payload.description}
        : {}),
      ...(typeof payload.visibility === 'string' && payload.visibility.length > 0
        ? {visibility: payload.visibility}
        : {}),
      ...(typeof primaryLanguage === 'string' && primaryLanguage.length > 0 ? {primaryLanguage} : {}),
    }
  } catch {
    return null
  }
}

/**
 * Query options for one repository's info. Shared by {@link useRepoInfo} and callers that load it
 * ahead of time, so both read the same cache entry with the same freshness rules and persister.
 */
export function repoInfoQueryOptions(repoId: number, {requireFullDetails = false, persister}: UseRepoInfoOptions = {}) {
  return queryOptions<RepoInfo | null>({
    queryKey: ['repo-info', repoId],
    queryFn: async ({client, queryKey}) => {
      const repoInfo = await fetchRepoInfo(repoId)
      return repoInfo ?? client.getQueryData<RepoInfo | null>(queryKey) ?? null
    },
    staleTime: (query: Query<RepoInfo | null>) =>
      requireFullDetails && (query.state.data === null || query.state.data?.partial === true) ? 0 : Infinity,
    ...(persister ? {persister} : {}),
  })
}

/**
 * Hook to fetch repository info for a set of repository IDs.
 * Returns a map of repository ID to RepoInfo, loading state, and error state.
 */
export function useRepoInfo(repoIds: number[], {requireFullDetails = false, persister}: UseRepoInfoOptions = {}) {
  // Deduplicate and sort IDs for stable query keys
  const uniqueIds = useMemo(() => [...new Set(repoIds)].sort((a, b) => a - b), [repoIds])

  const queries = useQueries({
    queries: uniqueIds.map(repoId => repoInfoQueryOptions(repoId, {requireFullDetails, persister})),
  })

  // Extract stable values from queries to use in dependencies
  const queryData = queries.map(q => q.data)

  // Build the repo map from query results
  const repoMap = useMemo(() => {
    const map: Record<number, RepoInfo> = {}
    for (const [index, data] of queryData.entries()) {
      const repoId = uniqueIds[index]
      if (data && repoId !== undefined) {
        map[repoId] = data
      }
    }
    return map
  }, [queryData, uniqueIds])

  const isLoading = queries.some(q => q.isLoading)
  const isFetching = queries.some(q => q.isFetching)
  const isError = queries.some(q => q.isError)

  return {data: repoMap, isLoading, isFetching, isError}
}
